import { Component } from '@theme/component';
import { sectionRenderer } from '@theme/section-renderer';
import { requestIdleCallback, viewTransition, yieldToMainThread } from '@theme/utilities';
import { PaginatedListAspectRatioHelper } from '@theme/paginated-list-aspect-ratio';
import { StandardEvents } from '@shopify/events';
import { getScrollTop, scrollTo, getIntersectionRoot } from '@theme/scroll-container';

/**
 * Resolves once the browser has painted the current frame.
 * @returns {Promise<void>}
 */
const afterNextPaint = () => new Promise((resolve) => requestAnimationFrame(() => setTimeout(resolve, 0)));

/**
 * A custom element that renders a paginated list of items.
 *
 * @typedef {object} Refs
 * @property {HTMLUListElement} [grid] - The grid element.
 * @property {HTMLSpanElement} [viewMorePrevious] - The view more previous button.
 * @property {HTMLSpanElement} [viewMoreNext] - The view more next button.
 * @property {HTMLElement[]} [cards] - The cards elements.
 * @property {HTMLElement} [loadMoreControls] - Load-more mode: wrapper carrying shown/total/template data.
 * @property {HTMLElement} [loadMoreCount] - Load-more mode: the "X of N products" counter.
 * @property {HTMLElement} [loadMoreBar] - Load-more mode: the progress bar fill.
 * @property {HTMLButtonElement} [loadMoreButton] - Load-more mode: the "Load more" button.
 * @property {HTMLTemplateElement} [loadMoreSkeleton] - Load-more mode: the placeholder card held in the grid while a page is fetched.
 *
 * @extends Component<Refs>
 */
export default class PaginatedList extends Component {
  /**
   * Prefetched pages, already parsed into their card elements.
   * @type {Map<number, Element[]>}
   */
  pages = new Map();

  /** @type {IntersectionObserver | undefined} */
  infinityScrollObserver;

  /** @type {((value: void) => void) | null} */
  #resolveNextPagePromise = null;

  /** @type {((value: void) => void) | null} */
  #resolvePreviousPagePromise = null;

  /** @type {PaginatedListAspectRatioHelper} */
  #aspectRatioHelper;

  /**
   * Bumped on every filter change. A page fetch remembers the value it started under and is
   * dropped if it no longer matches, so a response for the old filters can't land in the
   * cache after the change has cleared it.
   */
  #filterGeneration = 0;

  connectedCallback() {
    super.connectedCallback();

    /** @type {HTMLElement | null} */
    const templateCard = this.querySelector('[ref="cardGallery"]');
    if (templateCard) {
      this.#aspectRatioHelper = new PaginatedListAspectRatioHelper({
        templateCard,
      });
    }

    this.#fetchPage('next');
    this.#fetchPage('previous');
    this.#observeViewMore();

    // Listen for filter updates to clear cached pages
    document.addEventListener(StandardEvents.searchUpdate, this.#handleFilterUpdate);
    document.addEventListener(StandardEvents.collectionUpdate, this.#handleFilterUpdate);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    if (this.infinityScrollObserver) {
      this.infinityScrollObserver.disconnect();
    }
    // Remove the filter update listeners
    document.removeEventListener(StandardEvents.searchUpdate, this.#handleFilterUpdate);
    document.removeEventListener(StandardEvents.collectionUpdate, this.#handleFilterUpdate);
  }

  #observeViewMore() {
    const { viewMorePrevious, viewMoreNext } = this.refs;

    // Return if neither element exists
    if (!viewMorePrevious && !viewMoreNext) return;

    // Create observer if it doesn't exist
    if (!this.infinityScrollObserver) {
      this.infinityScrollObserver = new IntersectionObserver(
        async (entries) => {
          // Wait for any in-progress view transitions to finish
          if (viewTransition.current) await viewTransition.current;

          for (const entry of entries) {
            if (entry.isIntersecting) {
              // Use current refs to check which element triggered
              const { viewMorePrevious, viewMoreNext } = this.refs;

              if (entry.target === viewMorePrevious) {
                this.#renderPreviousPage();
              } else if (entry.target === viewMoreNext) {
                this.#renderNextPage();
              }
            }
          }
        },
        {
          // Desktop's real scroll container is `.page-wrapper`, not the viewport (see @theme/scroll-container).
          root: getIntersectionRoot(),
          rootMargin: '100px',
        }
      );
    }

    // Observe the view more elements
    if (viewMorePrevious) {
      this.infinityScrollObserver.observe(viewMorePrevious);
    }

    if (viewMoreNext) {
      this.infinityScrollObserver.observe(viewMoreNext);
    }
  }

  /**
   * @param {{ page: number, url?: URL } | undefined} pageInfo - The page info
   * @returns {boolean} Whether to use the page
   */
  #shouldUsePage(pageInfo) {
    if (!pageInfo) return false;

    const { grid } = this.refs;
    const lastPage = grid?.dataset.lastPage;

    if (!lastPage || pageInfo.page < 1 || pageInfo.page > Number(lastPage)) return false;

    return true;
  }

  /**
   * @param {"previous" | "next"} type
   */
  async #fetchPage(type) {
    const page = this.#getPage(type);

    // Always resolve the promise, even if we can't fetch the page
    const resolvePromise = () => {
      if (type === 'next') {
        this.#resolveNextPagePromise?.();
        this.#resolveNextPagePromise = null;
      } else {
        this.#resolvePreviousPagePromise?.();
        this.#resolvePreviousPagePromise = null;
      }
    };

    if (!page || !this.#shouldUsePage(page)) {
      // Resolve the promise even if we can't fetch
      resolvePromise();
      return;
    }

    // A failed fetch still resolves, or a render waiting on it (and the load-more
    // placeholders it holds in the grid) would wait for good. One overtaken by a filter
    // change doesn't: the change already resolved its waiters, and a resolver set since
    // belongs to a render of the new results.
    const generation = this.#filterGeneration;
    try {
      await this.#fetchSpecificPage(page.page, page.url);
    } finally {
      if (generation === this.#filterGeneration) resolvePromise();
    }
  }

  /**
   * @param {number} pageNumber - The page number to fetch
   * @param {URL} [url] - Optional URL, will be constructed if not provided
   */
  async #fetchSpecificPage(pageNumber, url = undefined) {
    const pageInfo = { page: pageNumber, url };

    if (!url) {
      const newUrl = new URL(window.location.href);
      newUrl.searchParams.set('page', pageNumber.toString());
      newUrl.hash = '';
      pageInfo.url = newUrl;
    }

    if (!this.#shouldUsePage(pageInfo)) return;
    const generation = this.#filterGeneration;
    const pageContent = await sectionRenderer.getSectionHTML(this.sectionId, true, pageInfo.url);

    // Parse while idle rather than on the click that renders the page: a whole
    // section document is a long task, and INP counts it if it runs there.
    await new Promise((resolve) => requestIdleCallback(resolve, { timeout: 500 }));

    // Stale: the filters changed while this was in flight - checked after the idle wait,
    // which widens that window. It happens on every collection visit: the next page is
    // prefetched on connect, before facets.js applies the availability filter, and caching
    // it made the first "Load more" append page 2 of the unfiltered list, whose boundaries
    // don't match the filtered page 1.
    if (generation !== this.#filterGeneration) return;

    const cards = this.#parseCards(pageContent);
    if (cards) this.pages.set(pageNumber, cards);
  }

  async #renderNextPage() {
    const { grid } = this.refs;

    if (!grid) return;

    const nextPage = this.#getPage('next');

    if (!nextPage || !this.#shouldUsePage(nextPage)) return;
    let nextPageItemElements = this.#getGridForPage(nextPage.page);

    /** @type {HTMLElement[]} */
    let placeholders = [];

    if (!nextPageItemElements) {
      const promise = new Promise((res) => {
        this.#resolveNextPagePromise = res;
      });

      // Trigger the fetch for this page. A failure resolves the promise below and leaves the
      // page uncached, which the render handles; there is nothing more to do with the error.
      this.#fetchPage('next').catch(() => {});

      // Load-more mode only: without its controls this inserts nothing.
      placeholders = this.#insertPlaceholders();

      await promise;
      nextPageItemElements = this.#getGridForPage(nextPage.page);

      // Swapped in the same task as the append below, so the browser never lays out the
      // grid without either of them and the content under it stays where it was.
      for (const placeholder of placeholders) placeholder.remove();

      if (!nextPageItemElements) {
        // The page never came: give back the button the placeholders may have hidden.
        if (placeholders.length && this.refs.loadMoreButton) this.refs.loadMoreButton.hidden = false;
        return;
      }
    }

    grid.append(...nextPageItemElements);

    this.#updateLoadMoreControls(nextPageItemElements.length, nextPage.page);

    this.#aspectRatioHelper.processNewElements();

    await yieldToMainThread();

    history.pushState('', '', nextPage.url.toString());

    requestIdleCallback(() => {
      this.#fetchPage('next');
    });
  }

  /**
   * Loads the next page on demand — the load-more mode's button, which replaces
   * the viewMoreNext sentinel of the infinite-scroll mode.
   */
  loadMore = async () => {
    const { loadMoreButton } = this.refs;

    // `pointer-events: none` stops a second click while busy, but not Enter or Space. A second
    // render would take over the pending page's resolver and strand the first one's placeholders.
    if (loadMoreButton?.getAttribute('aria-busy') === 'true') return;

    loadMoreButton?.setAttribute('aria-busy', 'true');
    try {
      // Paint the busy state before appending: the append connects a whole page
      // of card components in one task, and INP counts until the next paint.
      await afterNextPaint();
      await this.#renderNextPage();
    } finally {
      // Always, or the guard above would lock the button for good.
      loadMoreButton?.removeAttribute('aria-busy');
    }
  };

  /**
   * Holds the next page's place in the grid while it is fetched: skeleton cards the size
   * of the cards already there, one per product the page will bring.
   *
   * An uncached page can take most of a second, and cards landing that late push the
   * content under the grid down past the 500ms after an input that CLS forgives.
   * Placeholders move it now, while the click still covers the shift, and the cards then
   * take their place without moving it again.
   *
   * @returns {HTMLElement[]} The inserted placeholders, for the caller to remove.
   */
  #insertPlaceholders() {
    const { grid, cards, loadMoreControls, loadMoreButton, loadMoreSkeleton } = this.refs;
    const skeleton = loadMoreSkeleton?.content.firstElementChild;

    if (!grid || !Array.isArray(cards) || !loadMoreControls || !skeleton) return [];

    const lastCard = cards[cards.length - 1];
    if (!lastCard) return [];

    const { pageSize, total, shown } = loadMoreControls.dataset;
    const remaining = Number(total) - Number(shown);
    const count = Math.min(Number(pageSize), remaining);
    if (!(count > 0)) return [];

    // Every read before the first write, so the insert costs one layout.
    const height = lastCard.getBoundingClientRect().height;
    const gallery = lastCard.querySelector('.card-gallery');
    const ratio = gallery ? getComputedStyle(gallery).getPropertyValue('--gallery-aspect-ratio').trim() : '';
    const lastOrder = Number(lastCard.style.order) || cards.length;

    /** @type {HTMLElement[]} */
    const placeholders = [];

    for (let index = 1; index <= count; index++) {
      const placeholder = /** @type {HTMLElement} */ (skeleton.cloneNode(true));

      // Cards carry a catalogue-wide `order` (main-collection.liquid); without one of their
      // own the placeholders would sort to the front of the grid.
      placeholder.style.order = String(lastOrder + index);
      placeholder.style.setProperty('--load-more-placeholder-height', `${height}px`);
      if (ratio) placeholder.style.setProperty('--load-more-placeholder-ratio', ratio);
      placeholders.push(placeholder);
    }

    grid.append(...placeholders);

    // The last page hides the button once its cards land, which would move the content
    // under the controls up just as late. Hidden now, the click covers that move too.
    if (loadMoreButton && count === remaining) loadMoreButton.hidden = true;

    return placeholders;
  }

  /**
   * Syncs the load-more counter, progress bar and button with a freshly
   * appended page. No-op in the other pagination modes (refs absent). Only
   * next-page appends move the counter: prepends from a deep link don't change
   * how far into the total the visitor has loaded.
   * @param {number} appendedCount - Cards appended by this render.
   * @param {number} loadedPage - The page number that was appended.
   */
  #updateLoadMoreControls(appendedCount, loadedPage) {
    const { loadMoreControls, loadMoreCount, loadMoreBar, loadMoreButton, grid } = this.refs;

    if (!loadMoreControls) return;

    const total = Number(loadMoreControls.dataset.total) || 0;
    const shown = Math.min(Number(loadMoreControls.dataset.shown) + appendedCount, total);
    loadMoreControls.dataset.shown = String(shown);

    const template = loadMoreControls.dataset.labelTemplate;
    if (loadMoreCount && template) {
      loadMoreCount.textContent = template.replace('[shown]', String(shown)).replace('[total]', String(total));
    }

    if (loadMoreBar && total > 0) {
      loadMoreBar.style.width = `${(shown / total) * 100}%`;
    }

    const lastPage = Number(grid?.dataset.lastPage);
    if (loadMoreButton && lastPage && loadedPage >= lastPage) {
      loadMoreButton.hidden = true;
    }
  }

  async #renderPreviousPage() {
    const { grid } = this.refs;

    if (!grid) return;

    const previousPage = this.#getPage('previous');
    if (!previousPage || !this.#shouldUsePage(previousPage)) return;

    let previousPageItemElements = this.#getGridForPage(previousPage.page);
    if (!previousPageItemElements) {
      const promise = new Promise((res) => {
        this.#resolvePreviousPagePromise = res;
      });

      // Trigger the fetch for this page
      this.#fetchPage('previous');

      await promise;
      previousPageItemElements = this.#getGridForPage(previousPage.page);
      if (!previousPageItemElements) return;
    }

    // Store the current scroll position and height of the first element
    const currentScrollTop = getScrollTop();
    const firstElement = grid.firstElementChild;
    const oldHeight = firstElement ? firstElement.getBoundingClientRect().top + currentScrollTop : 0;

    // Prepend the new elements
    grid.prepend(...previousPageItemElements);

    this.#aspectRatioHelper.processNewElements();

    // Calculate and adjust scroll position to maintain the same view
    if (firstElement) {
      const newHeight = firstElement.getBoundingClientRect().top + getScrollTop();
      const heightDiff = newHeight - oldHeight;
      scrollTo({
        top: currentScrollTop + heightDiff,
        behavior: 'instant',
      });
    }

    await yieldToMainThread();

    history.pushState('', '', previousPage.url.toString());

    requestIdleCallback(() => {
      this.#fetchPage('previous');
    });
  }

  /**
   * @param {"previous" | "next"} type
   * @returns {{ page: number, url: URL } | undefined}
   */
  #getPage(type) {
    const { cards } = this.refs;
    const isPrevious = type === 'previous';

    if (!Array.isArray(cards)) return;

    const targetCard = cards[isPrevious ? 0 : cards.length - 1];

    if (!targetCard) return;

    const currentCardPage = Number(targetCard.dataset.page);
    const page = isPrevious ? currentCardPage - 1 : currentCardPage + 1;

    const url = new URL(window.location.href);
    url.searchParams.set('page', page.toString());
    url.hash = '';

    return {
      page,
      url,
    };
  }

  /**
   * @param {number} page
   * @returns {Element[] | undefined}
   */
  #getGridForPage(page) {
    const cards = this.pages.get(page);

    if (!cards) return;

    // The elements move into the grid on append, so a page is handed out once.
    this.pages.delete(page);
    return cards;
  }

  /**
   * @param {string} pageHTML - The section HTML of a fetched page
   * @returns {Element[] | undefined}
   */
  #parseCards(pageHTML) {
    const parsedPage = new DOMParser().parseFromString(pageHTML, 'text/html');
    const gridElement = parsedPage.querySelector('[ref="grid"]');
    if (!gridElement) return;
    return [...gridElement.querySelectorAll(':scope > [ref="cards[]"]')];
  }

  get sectionId() {
    const id = this.getAttribute('section-id');

    if (!id) throw new Error('The section-id attribute is required');

    return id;
  }

  /**
   * Handle filter updates by clearing cached pages.
   * Only reacts to events from the same section (ignores e.g. predictive search in the header).
   * @param {Event} event
   */
  #handleFilterUpdate = async (event) => {
    const target = /** @type {Element | null} */ (event.target);
    const eventSection = target?.closest('[id^="shopify-section-"]');
    // The PLP toolbar's size pills live in a section of their own, but plp-toolbar.js points
    // their form at this one, and the grid they re-render is this list's.
    const targetsThisList = target?.getAttribute?.('section-id') === this.sectionId;
    // Events from outside any section are not this list's either: header predictive search
    // renders from the layout, and each keystroke would otherwise refetch the next page.
    if (eventSection?.id !== `shopify-section-${this.sectionId}` && !targetsThisList) return;
    this.#filterGeneration += 1;
    this.pages.clear();

    // Resolve any pending promises to unblock waiting renders
    this.#resolveNextPagePromise?.();
    this.#resolvePreviousPagePromise?.();

    this.#resolveNextPagePromise = null;
    this.#resolvePreviousPagePromise = null;

    // The next page is fetched again once the event's promise says the new results are in,
    // whatever they look like. Waiting for the page count to change is not enough: the
    // availability filter facets.js applies on every collection load usually leaves it the
    // same, so the page prefetched on connect was discarded above and nothing replaced it,
    // and every first "Load more" waited on a fetch of its own.
    const generation = this.#filterGeneration;

    try {
      await /** @type {{ promise?: Promise<unknown> }} */ (event).promise;
    } catch {
      // A failed render still leaves results to prefetch from.
    }

    if (!this.isConnected || generation !== this.#filterGeneration) return;

    this.#observeViewMore();
    this.#fetchPage('next');
  };
}
